/**
 * plugins/endpoint.ts — Blocos de API endpoint (div.endpoint)
 *
 * Ferramentas: mudar método HTTP, URL, badge Novo/Deprecado,
 *              adicionar/mover/remover linhas de parâmetros na tabela.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside, moveSibling } from '../types';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

export class EndpointPlugin implements VtbPlugin {
    readonly panelId = 'vtb-endpoint';
    readonly selectorHints = ['div.endpoint', '.endpoint', '[data-vtb-id="endpoint"]'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e => 
            e.classList.contains('endpoint') || e.dataset.vtbId === 'endpoint'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Endpoint API', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;

        const method  = el.querySelector<HTMLElement>('.method')?.textContent?.trim() ?? 'GET';
        const url     = el.querySelector<HTMLElement>('.endpoint-url')?.textContent?.trim() ?? '';
        const badge   = el.querySelector<HTMLElement>('.endpoint-header .badge');

        const methodSel = registry.q<HTMLSelectElement>('vtb-ep-method');
        const urlIn     = registry.q<HTMLInputElement>('vtb-ep-url');
        const badgeSel  = registry.q<HTMLSelectElement>('vtb-ep-badge');

        if (methodSel) methodSel.value = HTTP_METHODS.includes(method as typeof HTTP_METHODS[number]) ? method : 'GET';
        if (urlIn)     urlIn.value     = url;

        let badgeVal = 'none';
        if (badge) {
            if (badge.classList.contains('badge-remove') || badge.textContent?.includes('Deprec')) badgeVal = 'deprecated';
            else badgeVal = 'new';
        }
        if (badgeSel) badgeSel.value = badgeVal;
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-ep-apply')?.addEventListener('click',      () => this._apply(registry));
        registry.q('vtb-ep-param-add')?.addEventListener('click',  () => this._addParam(registry));
        registry.q('vtb-ep-param-up')?.addEventListener('click',   () => this._moveParam(registry, -1));
        registry.q('vtb-ep-param-down')?.addEventListener('click', () => this._moveParam(registry,  1));
        registry.q('vtb-ep-param-del')?.addEventListener('click',  () => this._deleteParam(registry));
    }

    // ── Operações ─────────────────────────────────────────────────────────────

    private _apply(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el = ctx.el;

        const method = registry.q<HTMLSelectElement>('vtb-ep-method')?.value ?? 'GET';
        const url    = registry.q<HTMLInputElement>('vtb-ep-url')?.value?.trim() ?? '';
        const badge  = registry.q<HTMLSelectElement>('vtb-ep-badge')?.value ?? 'none';

        // Método
        const methodEl = el.querySelector<HTMLElement>('.method');
        if (methodEl) {
            methodEl.textContent = method;
            methodEl.className = 'method method-' + method.toLowerCase();
        }

        // URL
        const urlEl = el.querySelector<HTMLElement>('.endpoint-url');
        if (urlEl && url) urlEl.textContent = url;

        // Badge
        const header = el.querySelector<HTMLElement>('.endpoint-header');
        const existingBadge = header?.querySelector<HTMLElement>('.badge');
        if (badge === 'none') {
            existingBadge?.remove();
        } else if (header) {
            const b = existingBadge ?? document.createElement('span');
            b.className = badge === 'new' ? 'badge badge-new' : 'badge badge-remove';
            b.textContent = badge === 'new' ? 'Novo' : 'Deprecado';
            b.style.marginLeft = 'auto';
            if (!existingBadge) header.appendChild(b);
        }

        registry.sync();
    }

    private _addParam(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const tbody = ctx.el.querySelector<HTMLElement>('tbody');
        if (!tbody) return;

        const tr = document.createElement('tr');
        tr.innerHTML = '<td><code>param</code></td><td><em>string</em></td><td>Descrição</td>';
        tbody.appendChild(tr);
        registry.sync();
    }

    private _getSelectedParamRow(registry: IVtbRegistry): HTMLTableRowElement | null {
        const ctx = registry.activeCtx;
        if (!ctx) return null;
        // Seleciona a última linha da tabela de parâmetros como heurística simples
        const rows = Array.from(ctx.el.querySelectorAll<HTMLTableRowElement>('tbody tr'));
        // Usa a linha clicada (armazenada em `extra`)
        const idx = (ctx.extra?.paramRowIndex as number | undefined) ?? rows.length - 1;
        return rows[idx] ?? null;
    }

    private _moveParam(registry: IVtbRegistry, dir: -1 | 1): void {
        const row = this._getSelectedParamRow(registry);
        if (row) { moveSibling(row, dir); registry.sync(); }
    }

    private _deleteParam(registry: IVtbRegistry): void {
        const row = this._getSelectedParamRow(registry);
        if (row) { row.remove(); registry.sync(); }
    }
}
